import * as React from "react";
import { Layout } from "../layouts";
import { HeroWithLogos } from "../components/hero-with-logos";
import { BetterResults } from "../components/services/better-results";
import { Why } from "../components/services/why";
import { Campaign } from "../components/services/campaign";
import { Feature } from "../components/ui/cards/feature";
import { StaticImage } from "gatsby-plugin-image";
import { Process } from "../components/services/process";

import styled from "@emotion/styled";
import { Programs } from "../components/services/programs";
import { Partners } from "../components/services/partners";
import { Faqs } from "../components/ui/faqs/faqs";
import { Faq } from "../components/ui/faqs/faq";
import { breakpoints } from "../styles/breakpoints";

const StyledSeoPage = styled.div`
  .cards {
    display: flex;
    flex-direction: column;
    gap: var(--xx-small);

    @media (max-width: ${breakpoints.sm}) {
      align-items: center;
    }
  }

  .vertical-cards {
    display: flex;
    flex-wrap: wrap;
    gap: var(--xx-small);

    @media (max-width: ${breakpoints.md}) {
      flex-direction: column;
      align-items: center;
    }
  }
`;

const SEOPage = () => (
  <StyledSeoPage>
    <Layout
      title="White Label SEO Services for Agencies | 100% USA-Based"
      description="White Label SEO Services for ambitious agency owners, 100% USA-Based. Get our pricing and schedule your free consultation now! We know white label SEO!"
    >
      <HeroWithLogos
        title="White Label SEO Services to Dominate Page 1 of Google"
        text="Successful marketing agencies choose Agency Elevation for white label SEO services to improve client results, increase page 1 rankings, and generate a positive ROI."
      />
      <BetterResults />
      <Why />
      <Campaign />

      <div className="row">
        <Feature
          title="100% USA-Based Team"
          headingType="h2"
          text="Having a 100% USA-based team is one of our secrets to success. Our team is able to work with your team and agency during business hours, respond almost instantly, and understand all aspects of a campaign to ensure optimal results. We monitor SEO trends daily and apply cutting-edge and innovative strategies frequently to ensure our white label SEO packages are always moving the needle instead of focusing on tasks or busy work that have zero impact. In addition to being 100% USA-based, our SEO team of experts are extremely experienced in all areas of SEO campaigns including on-page optimizations, title tags, meta descriptions, keyword research, internal linking, GBP optimizations, link building, content marketing, schema, and more."
          inverted={true}
          noBg={true}
        >
          <StaticImage src="../images/seo/usa.png" alt="100% USA-Based Team" />
        </Feature>

        <Feature
          title="Real-Time Communication"
          headingType="h2"
          text="We integrate with you via Slack giving our team the same feel as an in-house employee that you can tag directly in Slack during business hours for near-instant responses and super responsive communication you'd rarely find with a white label SEO provider. Need a call or meeting to go over things? Just request it in Slack and our team will find a time that works for you to go over things in-depth. We are here for you in all aspects!"
          noBg={true}
        >
          <StaticImage
            src="../images/seo/realtime-communication.png"
            alt="Real-Time Communication"
          />
        </Feature>

        <Feature
          title="24/7 Reporting Dashboards"
          headingType="h2"
          text="Reporting is something that separates us from our competitors. Running a local SEO campaign? We track organic keyword rankings, geogrid (heatmap) rankings, traffic, Google Business Profile Insights data, phone calls, form submissions, overall visibility, and many more metrics. We also provide a custom portal login that’ll allow you and members of your team to login to our system and view projects, clients, reporting, billing, files, and much more; a true custom system designed to provide optimal data for your team with only a few clicks. We also provide a white label reporting dashboard customized with your agency’s logo that includes all aspects of SEO reporting on one web-based URL that you and your clients can bookmark. This is in conjunction with Google Sheets, Google Analytics, Google Search Console, and all of the various PDF and other spreadsheet reports our team uses on a monthly basis to get your clients results."
          inverted={true}
          noBg={true}
        >
          <StaticImage
            src="../images/seo/reporting-dashboards.png"
            alt="24/7 Reporting Dashboards"
          />
        </Feature>
      </div>

      <Process />

      <Programs />

      <Partners />

      <Faqs title="White Label SEO Frequently Asked Questions">
        <Faq title="What Are White Label SEO Services?">
          <p>
            So what exactly are white label SEO provider services? The phrases
            white label, private label, SEO reseller, and other terms all refer
            to the act of having an agency other than your own perform your
            clients' search engine optimization services. This means you're able
            to sell SEO services to your clients without needing to worry about
            fulfillment of the SEO work, managing an SEO team, hiring and
            firing, and other hurdles that come with trying to have an in-house
            team. Selling is typically the easy part, which makes white label
            SEO companies & services so beneficial for agencies, SEO
            consultants, and advertising firms that need to offer SEO but can’t
            staff an entire SEO team in-house.
          </p>

          <p>
            All work on your clients' websites and Google Business Profile (GBP)
            listings are done under white label logins and email addresses so
            Agency Elevation remains hidden and your clients never know they
            exist. As far as your clients are concerned, it's your team doing
            the work and with how tightly we integrate, it's true! SEO can be
            incredibly challenging and oftentimes the services need to be
            adjusted to keep up with the latest trends, algorithm changes, and
            more. When you partner with Agency Elevation as your white label SEO
            company, you’ll remove the hurdles associated with this and can hop
            over the pitfalls and focus only on selling and scaling your agency.
          </p>
        </Faq>

        <Faq title="Why Should My Agency Use White Label SEO services?">
          <p>
            Attracting, interviewing, hiring, onboarding, compensating, and
            keeping in-house SEO staff is a tough chore. It takes valuable time
            and several roles to run effective SEO campaigns. Can your team
            write content? Can your SEO team build links? Can they optimize
            websites and Google Business Profile listings? Can you afford
            continual raises? Costly health insurance? Equipment expenses? All
            the personal time you'll need to give them in the way of regular
            check-ins and periodic reviews?
          </p>

          <p>
            Another common issue with in-house SEO teams is how will you handle
            overflow? Let’s say you hire someone to handle 20 accounts, and
            they’re maxed out right away. What happens when you have five more
            accounts? It's too much for your current employee, but not enough to
            justify another hire. Our white label SEO services can scale up or
            down from one client to 100 on a moment’s notice, and without you
            paying a cent more than needed. The flexibility of scale and
            maximizing resources is a major benefit to white label SEO services
            for agencies.
          </p>

          <p>
            And yet still another challenge with in-house is the training
            aspect. Are you equipped to ensure your team is continually
            up-to-date with the latest SEO best practices, skills, and algorithm
            updates from Google? Google changes their algorithm very often and
            are you confident your team is up to date on the latest algorithm
            shifts? Unless you yourself are an expert and have a lot of time on
            your hands, you’re going to need to have them get their SEO training
            elsewhere. Maybe you'll have to pay for it, or only hope your team
            does it on their own time. We keep our team accountable for you;
            you'll have nothing to worry about with our white label SEO for
            agencies!
          </p>
        </Faq>

        <Faq title="How Do Your White Label SEO Services Work?">
          <p>
            It's super simple for you! Any time you get a new account, you'll
            just complete our easy SEO intake form which allows us to gather
            pertinent information from you about your client, and we’ll have the
            campaign ready to launch within two business days or so. We’ll pull
            everything together and communicate with you only as needed to keep
            it as hands off as possible for you.
          </p>

          <p>
            We can help you get your CallRail or other call tracking software
            setup right too, in addition to the proper placement of Google
            Analytics, CallRail code, Google Search Console, and more so all the
            pieces are in place for a successful launch. Our communication with
            you is quick and seamless, with no wondering if we’ll ever get back
            to you, and you’ll be integrated with us in Slack. We’re 100%
            USA-based and so we’ll work in the same time zone(s) as you and
            provide incredible customer service and a high level of awareness
            and transparency in terms of where things stand and how campaigns
            are performing.
          </p>

          <p>
            Our post-launch white label SEO for agencies entails a sophisticated
            optimization routine our team goes through weekly. We cover all the
            important aspects, including regular analysis of your clients’
            keyword rankings, Maps/GBP rankings, and ensuring no issues are
            present in Google Search Console; all the elements that go into a
            successful SEO campaign are included!
          </p>

          <p>
            Our reports are aesthetically pleasing and informative, and will
            show the key performance indicators (e.g. GBP calls, rankings, etc.)
            for your clients’ SEO results for whichever time frame you or they
            choose. This makes retention easy, and one less step for you to do!
            In addition to keyword rankings, we report on GBP signals, content
            created, and syndication of the content for the ultimate combination
            to rank your clients and retain them longer.
          </p>
        </Faq>

        <Faq title="Do You Use Black-hat or Risky SEO Techniques & Tactics?">
          <p>
            Absolutely not! Our white label SEO agency services for agencies
            hinge on a few important promises which are:
          </p>

          <ol>
            <li>
              We actually move the needle for your clients to get them positive
              ROI and results and help you retain them for years to come.
            </li>
            <li>
              We will never put you or your clients at risk by using black-hat
              SEO techniques that are against Google’s TOS (terms of service).
              Everything we do on your behalf is white hat, legitimate, and gets
              results.
            </li>
          </ol>
        </Faq>

        <Faq title="Do You Include Content in Your White Label SEO Services for Agencies?">
          <p>
            Yes! All of our SEO packages include content creation and as
            importantly, syndication! We create content for your clients by
            using 100% USA-based writers that read, write, and speak fluent
            English and have college degrees. We then take this content and
            distribute/syndicate it via our network of authoritative websites
            (lots of high-authority news sites) and embed GBP (Google Business
            Profile) signals into each syndication to boost not only organic
            rankings, but also Maps rankings. We also offer extra content at
            very competitive prices for any and all SEO purposes including
            service pages, geo-location pages, blog posts, articles, press
            releases, eBooks, white papers, and more!
          </p>
        </Faq>

        <Faq title="Do You Include GBP (Google Business Profile) Optimizations in Your White Label SEO Services for Agencies?">
          <p>
            Absolutely! All of our SEO packages include both GBP (Maps) and
            organic optimizations. This is done by optimizing the GBP listing
            AND website in conjunction for the best campaign performance, and
            our team is trained by the best in the industry on the latest GBP
            and Maps optimization techniques. We start with basic foundational
            local SEO optimizations and then as the campaign trends upward we
            apply advanced GBP SEO to get your clients’ listings in the 3-pack
            (top three results for local Maps searches) to increase call volume,
            leads, and visibility.
          </p>
        </Faq>

        <Faq title="Do You Include Link Building in Your White Label SEO Services for Agencies?">
          <p>
            Yes! All of our clients receive monthly link building. All of the
            links we create for your clients are legitimate and white hat. Most
            of the links are from writing good content and getting that content
            distributed through real websites with real traffic. The quality of
            links is high and really helps in pushing your clients’ SEO and
            Domain Authority through the roof for the ultimate in boosting
            rankings and results.
          </p>
        </Faq>

        <Faq title="Why are Agency Elevation’s White Label SEO Services Better Than Its Competitors’?">
          <p>
            We’re local, and you'll be supporting an American business. We’ve
            found most white label SEO agency services may have a US-based
            office or two, but the work is outsourced overseas where quality
            really suffers. All of our employees are based in the United States,
            primarily in our central locations of Wisconsin and Pennsylvania,
            but with team members in many other states as well. We provide the
            utmost quality in our white label SEO services for agencies, and
            incredible timeliness in communication, which are two absolutely
            critical components for the success of any agency’s SEO fulfillment.
          </p>

          <p>
            Don't let us being US based fool you into thinking we are expensive
            though! Our pricing is as competitive as it gets, straightforward,
            and simple. No setup fees, hidden expenses, or charges at every
            corner; we charge just enough to be able to move the needle for your
            clients and not a penny more.
          </p>

          <p>
            We’ve mentioned the quality of our work, and there’s a reason why.
            Our team has experience in white label SEO for agencies that few
            possess, namely doing work for many agencies that require actual
            results and moving the needle for their clients. Learnings gleaned
            from this paired with managing thousands of small to medium-sized
            business’ SEO campaigns over the years have led us to create a
            special blueprint for success that we’d love to perform on your
            clients’ accounts! We also have tremendous experience in national
            and eCommerce SEO as well, providing the full gamut of SEO
            capabilities.
          </p>
        </Faq>

        <Faq title="When Could I Begin Your White Label SEO Services?">
          <p>
            Quickly! Click on our get pricing button to get our affordable,
            agency based pricing and from there, schedule your free
            consultation. We can often go live with a new SEO account within two
            business days of receiving all of your information.
          </p>
        </Faq>
      </Faqs>
    </Layout>
  </StyledSeoPage>
);

export default SEOPage;
